/**
 * TeamSystem Impact - Sustainable Platform Dashboard
 * Dashboard KPI con 5 sezioni: SIA, GSF, Carbon-Aware, SCI, Cloud Emissions
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Alert,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Nature as EcoIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  TrendingDown as TrendingDownIcon,
  Speed as SpeedIcon,
  Bolt as BoltIcon,
  Cloud as CloudIcon,
  WaterDrop as WaterDropIcon,
  Forest as ForestIcon,
  Description as DescriptionIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config';
import { useWorkingYear } from '../../store/workingYearStore';
import { useInfrastructureStore, PROVIDER_PRESETS, DEFAULT_CONFIG } from '../../store/infrastructureStore';
import {
  type Status,
  SIA_ROWS,
  SIA_PILLARS,
  GSF_PRINCIPLES,
  OVERALL_GSF_SCORE,
  GSF_MATURITY_LEVEL,
  GSF_MATURITY_LABEL,
  GSF_TARGET_LEVEL,
  GSF_TARGET_LABEL,
  CARBON_INTENSITY,
  CARBON_REGION,
  CARBON_STRATEGIES,
  SCI_PAGE_VIEW,
  SCI_PDF,
  SCI_ANNUAL,
  PAGE_VIEW_ENERGY,
  PDF_ENERGY,
  MONTHLY_BREAKDOWN,
  KEY_INSIGHTS,
  PRIORITY_ACTIONS,
  CLOUD_PROVIDER,
  CLOUD_INSTANCE,
  CLOUD_DATABASE,
  SERVER_LIFETIME_YEARS,
  ITEU_COMPONENTS,
  TFE_ANNUAL_KWH,
  TOTAL_CLOUD_EMISSIONS_LOCATION,
  TOTAL_CLOUD_EMISSIONS_MARKET,
  WATER_TOTAL_LITERS,
  GHG_SCOPES,
  EMISSION_METHODS,
  PUE_COMPARISON,
  EMBODIED_BREAKDOWN,
  EMBODIED_USER_SHARE,
  EMBODIED_SERVER_KG,
  AZURE_WUE,
  REGION_ELECTRICITY_WUE,
  CARBON_PUE,
  TOTAL_BIODIV_IMPACT,
  BIODIV_PATHWAYS,
  TNFD_PHASES,
  SITING_CRITERIA,
  MATERIAL_CFS,
  SIA_A5_BIODIVERSITY,
  TOTAL_MATERIAL_IMPACT,
  LAND_USE_IMPACT,
  DATACENTER_AREA_M2,
  LAND_USE_CF,
} from './data/sustainabilityData';

function statusColor(status: Status): string {
  switch (status) {
    case 'PASS': case 'EXCELLENT': return 'success.main';
    case 'PARTIAL': return 'warning.main';
    case 'NEEDS WORK': return 'error.main';
    default: return 'text.disabled';
  }
}

function statusBgColor(status: Status): string {
  switch (status) {
    case 'PASS': case 'EXCELLENT': return 'success.light';
    case 'PARTIAL': return 'warning.light';
    case 'NEEDS WORK': return 'error.light';
    default: return 'grey.200';
  }
}

function StatusChip({ status }: { status: Status }) {
  const icon = status === 'PASS' || status === 'EXCELLENT'
    ? <CheckIcon sx={{ fontSize: 14 }} />
    : status === 'PARTIAL'
      ? <WarningIcon sx={{ fontSize: 14 }} />
      : status === 'NEEDS WORK'
        ? <ErrorIcon sx={{ fontSize: 14 }} />
        : undefined;

  return (
    <Chip
      size="small"
      label={status}
      icon={icon}
      sx={{
        fontWeight: 600,
        fontSize: '0.7rem',
        color: statusColor(status),
        bgcolor: statusBgColor(status),
      }}
    />
  );
}

function SectionTitle({ icon, title, id }: { icon: React.ReactNode; title: string; id?: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }} id={id}>
      {icon}
      <Typography variant="h6" component="h2" fontWeight="bold">{title}</Typography>
    </Box>
  );
}

// ─── Section 1: SIA ──────────────────────────────────────
function SIASection({ lang }: { lang: string }) {
  const { t } = useTranslation();
  return (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }} role="region" aria-labelledby="section-sia">
      <SectionTitle
        id="section-sia"
        icon={<EcoIcon color="success" />}
        title={t('settings.sustainablePlatformPage.siaTitle')}
      />

      {/* Three pillars */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {SIA_PILLARS.map((p) => (
          <Grid item xs={12} sm={4} key={p.label}>
            <Card variant="outlined" sx={{ borderTop: 3, borderTopColor: statusColor(p.status) }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {lang === 'it' ? p.labelIT : p.label}
                </Typography>
                <Box sx={{ my: 1 }}>
                  <StatusChip status={p.status} />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {lang === 'it' ? p.detailIT : p.detail}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* SIA table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600, width: '25%' }}>{i18n.t('settings.sustainablePlatform.area')}</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '15%' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                {i18n.t('settings.sustainablePlatform.notes')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {SIA_ROWS.map((row, i) => (
              <TableRow key={row.area} sx={{ bgcolor: i % 2 === 0 ? 'transparent' : 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{row.area}</TableCell>
                <TableCell><StatusChip status={row.status} /></TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>{row.notes}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

// ─── Section 2: GSF Principles ───────────────────────────
function GSFSection({ lang }: { lang: string }) {
  void lang;
  const { t } = useTranslation();
  return (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }} role="region" aria-labelledby="section-gsf">
      <SectionTitle
        id="section-gsf"
        icon={<BoltIcon color="primary" />}
        title={t('settings.sustainablePlatformPage.gsfTitle')}
      />

      {/* Principle cards grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {GSF_PRINCIPLES.map((p) => (
          <Grid item xs={12} sm={6} md={4} key={p.principle}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                    {p.principle}
                  </Typography>
                  <StatusChip status={p.status} />
                </Box>
                {p.status !== 'N/A' && (
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">{p.score}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(p.numericScore / p.maxScore) * 100}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: statusColor(p.status),
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary">
                  {p.keyFinding}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Summary KPIs */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined" sx={{ borderTop: 3, borderTopColor: 'primary.main' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">Overall Score</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '2rem' }} color="primary.main">
                {OVERALL_GSF_SCORE}/5
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined" sx={{ borderTop: 3, borderTopColor: 'warning.main' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">GSF Maturity</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.5rem' }} color="warning.main">
                Level {GSF_MATURITY_LEVEL}
              </Typography>
              <Typography variant="caption" color="text.secondary">{GSF_MATURITY_LABEL}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined" sx={{ borderTop: 3, borderTopColor: 'success.main' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">Target</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.5rem' }} color="success.main">
                Level {GSF_TARGET_LEVEL}
              </Typography>
              <Typography variant="caption" color="text.secondary">{GSF_TARGET_LABEL} (6 months)</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
}

// ─── Section 3: Carbon-Aware Architecture ────────────────
function CarbonAwareSection({ lang }: { lang: string }) {
  void lang;
  const { t } = useTranslation();
  return (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }} role="region" aria-labelledby="section-carbon">
      <SectionTitle
        id="section-carbon"
        icon={<TrendingDownIcon color="info" />}
        title={t('settings.sustainablePlatformPage.carbonAwareTitle')}
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ borderTop: 3, borderTopColor: 'info.main' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {i18n.t('settings.sustainablePlatform.region')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.25rem' }}>{CARBON_REGION}</Typography>
              <Typography variant="body2" color="text.secondary">
                {CARBON_INTENSITY} gCO2eq/kWh
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ borderTop: 3, borderTopColor: 'success.main' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                {i18n.t('settings.sustainablePlatform.reductionStrategies')}
              </Typography>
              {CARBON_STRATEGIES.map((s) => (
                <Box key={s.strategy} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption">{s.strategy}</Typography>
                  <Chip size="small" label={s.impact} sx={{ fontSize: '0.65rem', height: 20 }} />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
}

// ─── Section 4: SCI Measurement ──────────────────────────
function SCISection({ lang }: { lang: string }) {
  void lang;
  const { t } = useTranslation();
  return (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }} role="region" aria-labelledby="section-sci">
      <SectionTitle
        id="section-sci"
        icon={<SpeedIcon color="secondary" />}
        title={t('settings.sustainablePlatformPage.sciTitle')}
      />

      {/* KPI cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined" sx={{ borderTop: 3, borderTopColor: 'success.main' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">SCI — Page View</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '2rem' }} color="success.main">{SCI_PAGE_VIEW}</Typography>
              <Typography variant="caption" color="text.secondary">gCO2eq / page view</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined" sx={{ borderTop: 3, borderTopColor: 'primary.main' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">SCI — PDF Generation</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '2rem' }} color="primary.main">{SCI_PDF}</Typography>
              <Typography variant="caption" color="text.secondary">gCO2eq / PDF</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined" sx={{ borderTop: 3, borderTopColor: 'text.secondary' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {i18n.t('settings.sustainablePlatform.annualEstimate')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '2rem' }}>~{SCI_ANNUAL}</Typography>
              <Typography variant="caption" color="text.secondary">gCO2eq/year</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Energy breakdown tables */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            {i18n.t('settings.sustainablePlatform.energyPageView')}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Component</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Value</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Unit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {PAGE_VIEW_ENERGY.map((r) => (
                  <TableRow key={r.label}>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: r.label === 'E total' ? 700 : 400 }}>{r.label}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: r.label === 'E total' ? 700 : 400 }}>{r.value}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{r.unit}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            {i18n.t('settings.sustainablePlatform.energyPdf')}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Component</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Value</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Unit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {PDF_ENERGY.map((r) => (
                  <TableRow key={r.label}>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: r.label === 'E total' ? 700 : 400 }}>{r.label}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: r.label === 'E total' ? 700 : 400 }}>{r.value}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{r.unit}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* Monthly breakdown */}
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {i18n.t('settings.sustainablePlatform.monthlyEstimate')}
      </Typography>
      <TableContainer sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                {i18n.t('settings.sustainablePlatform.activity')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>SCI</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                {i18n.t('settings.sustainablePlatform.volumeMonth')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                {i18n.t('settings.sustainablePlatform.monthlyTotal')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {MONTHLY_BREAKDOWN.map((r) => (
              <TableRow key={r.activity}>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: r.activity === 'Total' ? 700 : 400 }}>{r.activity}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{r.sci}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{r.volume}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: r.activity === 'Total' ? 700 : 400 }}>{r.total}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Key Insights */}
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Key Insights</Typography>
      <Box sx={{ mb: 3 }}>
        {KEY_INSIGHTS.map((insight, i) => (
          <Typography key={i} variant="body2" color="text.secondary" sx={{ mb: 0.5, pl: 2, position: 'relative', '&::before': { content: '"\\2022"', position: 'absolute', left: 0 } }}>
            {insight}
          </Typography>
        ))}
      </Box>

      {/* Priority Actions */}
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {i18n.t('settings.sustainablePlatform.priorityActions')}
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', width: '5%' }}>#</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', width: '45%' }}>
                {i18n.t('settings.sustainablePlatform.action')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', width: '12%' }}>
                {i18n.t('settings.sustainablePlatform.priority')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                {i18n.t('settings.sustainablePlatform.expectedImpact')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {PRIORITY_ACTIONS.map((a) => (
              <TableRow key={a.n}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{a.n}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{a.action}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={a.priority}
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      height: 20,
                      color: a.priority === 'High' ? 'error.main' : a.priority === 'Medium' ? 'warning.main' : 'text.secondary',
                      bgcolor: a.priority === 'High' ? 'error.light' : a.priority === 'Medium' ? 'warning.light' : 'grey.200',
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{a.impact}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

// ─── Section 5: Cloud Emission Measurement ───────────────
function CloudEmissionSection({ lang }: { lang: string }) {
  const { t } = useTranslation();
  return (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }} role="region" aria-labelledby="section-cloud">
      <SectionTitle
        id="section-cloud"
        icon={<CloudIcon color="primary" />}
        title={t('settings.sustainablePlatformPage.cloudEmissionTitle')}
      />

      {/* Summary KPI cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card variant="outlined" sx={{ borderTop: 3, borderTopColor: 'success.main' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {i18n.t('settings.sustainablePlatform.cloudEmissionsLocation')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.5rem' }} color="success.main">
                {TOTAL_CLOUD_EMISSIONS_LOCATION}
              </Typography>
              <Typography variant="caption" color="text.secondary">gCO2eq/year</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card variant="outlined" sx={{ borderTop: 3, borderTopColor: 'primary.main' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {i18n.t('settings.sustainablePlatform.cloudEmissionsMarket')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.5rem' }} color="primary.main">
                {TOTAL_CLOUD_EMISSIONS_MARKET}
              </Typography>
              <Typography variant="caption" color="text.secondary">gCO2eq/year</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card variant="outlined" sx={{ borderTop: 3, borderTopColor: 'info.main' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                <WaterDropIcon sx={{ fontSize: 14, color: 'info.main' }} />
                <Typography variant="caption" color="text.secondary">
                  {i18n.t('settings.sustainablePlatform.waterUsage')}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.5rem' }} color="info.main">
                {WATER_TOTAL_LITERS * 1000}
              </Typography>
              <Typography variant="caption" color="text.secondary">mL/year</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card variant="outlined" sx={{ borderTop: 3, borderTopColor: 'text.secondary' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {t('settings.sustainablePlatformPage.embodiedHardware')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
                {EMBODIED_USER_SHARE}
              </Typography>
              <Typography variant="caption" color="text.secondary">gCO2eq/year</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Infrastructure boundary */}
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {i18n.t('settings.sustainablePlatform.infrastructureBoundary')}
      </Typography>
      <TableContainer sx={{ mb: 3 }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', width: '25%' }}>Cloud Provider</TableCell>
              <TableCell sx={{ fontSize: '0.8rem' }}>{CLOUD_PROVIDER}</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Backend</TableCell>
              <TableCell sx={{ fontSize: '0.8rem' }}>{CLOUD_INSTANCE} — Node 20 / Fastify</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Database</TableCell>
              <TableCell sx={{ fontSize: '0.8rem' }}>{CLOUD_DATABASE}</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Frontend</TableCell>
              <TableCell sx={{ fontSize: '0.8rem' }}>Static files via CDN (Vite build)</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                {i18n.t('settings.sustainablePlatform.serverLifetime')}
              </TableCell>
              <TableCell sx={{ fontSize: '0.8rem' }}>{SERVER_LIFETIME_YEARS} {i18n.t('settings.sustainablePlatform.years')} (Azure published)</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* ITEU Decomposition + PUE comparison side by side */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            ITEU {i18n.t('settings.sustainablePlatform.decomposition')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            IT Electricity Usage = Compute + Storage + Networking
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    {i18n.t('settings.sustainablePlatform.category')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>kWh/year</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>%</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    {i18n.t('settings.sustainablePlatform.detail')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ITEU_COMPONENTS.map((c, i) => (
                  <TableRow key={c.category} sx={{ bgcolor: i % 2 === 0 ? 'transparent' : 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                      {lang === 'it' ? c.categoryIT : c.category}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{c.energyKwh.toFixed(5)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={c.percentage}
                          sx={{
                            width: 50, height: 6, borderRadius: 3, bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': { bgcolor: 'primary.main', borderRadius: 3 },
                          }}
                        />
                        {c.percentage}%
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{c.detail}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>TFE (ITEU x PUE {CARBON_PUE})</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{TFE_ANNUAL_KWH.toFixed(4)}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>100%</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            PUE — Power Usage Effectiveness
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            PUE = Total Facility Energy / IT Equipment Energy
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Provider</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>PUE</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {PUE_COMPARISON.map((p, i) => (
                  <TableRow
                    key={p.provider}
                    sx={{
                      bgcolor: p.provider.includes('Azure') ? 'primary.50' : i % 2 === 0 ? 'transparent' : 'grey.50',
                      ...(p.provider.includes('Azure') ? { border: '2px solid', borderColor: 'primary.main' } : {}),
                    }}
                  >
                    <TableCell sx={{ fontWeight: p.provider.includes('Azure') ? 700 : 400, fontSize: '0.75rem' }}>
                      {p.provider}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{p.pue}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{p.notes}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* Location vs Market + GHG Scopes side by side */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            {t('settings.sustainablePlatformPage.locationVsMarketTitle')}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    {i18n.t('settings.sustainablePlatform.method')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    {i18n.t('settings.sustainablePlatform.value')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    {i18n.t('settings.sustainablePlatform.source')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {EMISSION_METHODS.map((m) => (
                  <TableRow key={m.method}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{m.method}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{m.value} {m.unit}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{m.source}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Alert severity="info" sx={{ mt: 1, py: 0 }}>
            <Typography variant="caption">
              {t('settings.sustainablePlatformPage.csrdBothMethodsNote')}
            </Typography>
          </Alert>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            GHG Scope Mapping
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Scope</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    {i18n.t('settings.sustainablePlatform.description')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    {i18n.t('settings.sustainablePlatform.value')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{i18n.t('settings.sustainablePlatform.notes')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {GHG_SCOPES.map((s, i) => (
                  <TableRow key={s.scope} sx={{ bgcolor: i % 2 === 0 ? 'transparent' : 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{s.scope}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      {lang === 'it' ? s.descriptionIT : s.description}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{s.value}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      {lang === 'it' ? s.notesIT : s.notes}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* Embodied emissions breakdown */}
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {i18n.t('settings.sustainablePlatform.embodiedLifecycle')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('settings.sustainablePlatformPage.embodiedLifecycleCaption', {
          serverKg: EMBODIED_SERVER_KG,
          vcpu: 64,
          years: SERVER_LIFETIME_YEARS,
          userShare: EMBODIED_USER_SHARE,
        })}
      </Typography>
      <Grid container spacing={1} sx={{ mb: 2 }}>
        {EMBODIED_BREAKDOWN.map((b) => (
          <Grid item xs={6} sm key={b.component}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                  {b.component}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.25rem' }} color="text.secondary">
                  {b.percentage}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  {b.detail}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Water detail */}
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <WaterDropIcon sx={{ fontSize: 16, color: 'info.main' }} />
          {i18n.t('settings.sustainablePlatform.waterConsumption')}
        </Box>
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                {i18n.t('settings.sustainablePlatform.source')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                {i18n.t('settings.sustainablePlatform.formula')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>L/year</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontSize: '0.75rem' }}>
                {i18n.t('settings.sustainablePlatform.datacenterCooling')}
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>TFE x WUE ({AZURE_WUE} L/kWh)</TableCell>
              <TableCell sx={{ fontSize: '0.75rem' }}>0.0117</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontSize: '0.75rem' }}>
                {i18n.t('settings.sustainablePlatform.electricityProduction')}
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>E x PUE x WUE_region ({REGION_ELECTRICITY_WUE} L/kWh)</TableCell>
              <TableCell sx={{ fontSize: '0.75rem' }}>0.0064</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                {i18n.t('settings.sustainablePlatform.total')}
              </TableCell>
              <TableCell />
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{WATER_TOTAL_LITERS} (~18 mL)</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

// ─── Section 6: Digital Biodiversity ─────────────────────
function BiodiversitySection({ lang }: { lang: string }) {
  const { t } = useTranslation();
  return (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }} role="region" aria-labelledby="section-biodiversity">
      <SectionTitle
        id="section-biodiversity"
        icon={<ForestIcon sx={{ color: '#2e7d32' }} />}
        title={t('settings.sustainablePlatformPage.biodiversityTitle')}
      />

      {/* Summary KPI cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card variant="outlined" sx={{ borderTop: 3, borderTopColor: 'success.main' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {i18n.t('settings.sustainablePlatform.totalImpact')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.25rem' }} color="success.main">
                {TOTAL_BIODIV_IMPACT.toExponential(1)}
              </Typography>
              <Typography variant="caption" color="text.secondary">species·yr/year</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card variant="outlined" sx={{ borderTop: 3, borderTopColor: 'warning.main' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {i18n.t('settings.sustainablePlatform.dominantPathway')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.25rem' }} color="warning.main">
                {i18n.t('settings.sustainablePlatform.materials')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {TOTAL_MATERIAL_IMPACT.toExponential(1)} species·yr
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card variant="outlined" sx={{ borderTop: 3, borderTopColor: 'info.main' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {i18n.t('settings.sustainablePlatform.landUse')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.25rem' }} color="info.main">
                {LAND_USE_IMPACT.toExponential(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">PDF·m²·yr</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card variant="outlined" sx={{ borderTop: 3, borderTopColor: 'primary.main' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">TNFD LEAP</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.25rem' }} color="primary.main">
                {i18n.t('settings.sustainablePlatform.initiated')}
              </Typography>
              <StatusChip status="PARTIAL" />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Impact pathways table */}
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {i18n.t('settings.sustainablePlatform.biodiversityPathways')}
      </Typography>
      <TableContainer sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Pathway</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                {i18n.t('settings.sustainablePlatform.value')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                {i18n.t('settings.sustainablePlatform.unit')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                {i18n.t('settings.sustainablePlatform.method')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{i18n.t('settings.sustainablePlatform.notes')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {BIODIV_PATHWAYS.map((p, i) => (
              <TableRow key={p.pathway} sx={{ bgcolor: i % 2 === 0 ? 'transparent' : 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                  {lang === 'it' ? p.pathwayIT : p.pathway}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{p.value}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{p.unit}</TableCell>
                <TableCell sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{p.method}</TableCell>
                <TableCell sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{p.notes}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Material extraction + TNFD side by side */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            {i18n.t('settings.sustainablePlatform.materialExtraction')}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    {i18n.t('settings.sustainablePlatform.material')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>CF (species·yr/kg)</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    {i18n.t('settings.sustainablePlatform.impact')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {MATERIAL_CFS.map((m, i) => (
                  <TableRow key={m.material} sx={{ bgcolor: i % 2 === 0 ? 'transparent' : 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{m.material}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{m.cfSpeciesYrPerKg.toExponential(1)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{m.impact.toExponential(1)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            TNFD LEAP Process
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    {i18n.t('settings.sustainablePlatform.phase')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    {i18n.t('settings.sustainablePlatform.action')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {TNFD_PHASES.map((t, i) => (
                  <TableRow key={t.phase} sx={{ bgcolor: i % 2 === 0 ? 'transparent' : 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                      {lang === 'it' ? t.phaseIT : t.phase}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem' }}>
                      {lang === 'it' ? t.actionIT : t.action}
                    </TableCell>
                    <TableCell><StatusChip status={t.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* Datacenter siting evaluation */}
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {i18n.t('settings.sustainablePlatform.datacenterSiting')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('settings.sustainablePlatformPage.datacenterSitingCaption', {
          area: DATACENTER_AREA_M2.toLocaleString(),
          cf: LAND_USE_CF,
        })}
      </Typography>
      <TableContainer sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                {i18n.t('settings.sustainablePlatform.criterion')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                {i18n.t('settings.sustainablePlatform.ourValue')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                {i18n.t('settings.sustainablePlatform.risk')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{i18n.t('settings.sustainablePlatform.notes')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {SITING_CRITERIA.map((c, i) => (
              <TableRow key={c.criterion} sx={{ bgcolor: i % 2 === 0 ? 'transparent' : 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                  {lang === 'it' ? c.criterionIT : c.criterion}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{c.ourValue}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={c.rating}
                    sx={{
                      fontSize: '0.65rem', fontWeight: 600, height: 20,
                      color: c.rating === 'Low' ? 'success.main' : c.rating === 'Medium' ? 'warning.main' : 'error.main',
                      bgcolor: c.rating === 'Low' ? 'success.light' : c.rating === 'Medium' ? 'warning.light' : 'error.light',
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{c.notes}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* SIA A5 Biodiversity checklist */}
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {i18n.t('settings.sustainablePlatform.biodiversityChecklist')}
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                {i18n.t('settings.sustainablePlatform.item')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', width: '15%' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {SIA_A5_BIODIVERSITY.map((a, i) => (
              <TableRow key={a.item} sx={{ bgcolor: i % 2 === 0 ? 'transparent' : 'grey.50' }}>
                <TableCell sx={{ fontSize: '0.8rem' }}>
                  {lang === 'it' ? a.itemIT : a.item}
                </TableCell>
                <TableCell><StatusChip status={a.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

// ─── Infrastructure Configuration ─────────────────────────
function InfrastructureConfigSection({ lang }: { lang: string }) {
  void lang; // lang non più usato dopo la migrazione a i18n.t()
  const { config, setConfig, resetToDefaults } = useInfrastructureStore();
  const [expanded, setExpanded] = useState(false);

  const providers = Object.keys(PROVIDER_PRESETS);
  const regions = PROVIDER_PRESETS[config.provider]?.regions || [];

  const handleProviderChange = (provider: string) => {
    const preset = PROVIDER_PRESETS[provider];
    if (preset && preset.regions.length > 0) {
      const first = preset.regions[0];
      setConfig({
        provider,
        region: first.name,
        carbonIntensity: first.carbonIntensity,
        pue: first.pue,
        wue: first.wue,
      });
    } else {
      setConfig({ provider });
    }
  };

  const handleRegionChange = (regionName: string) => {
    const preset = regions.find((r) => r.name === regionName);
    if (preset) {
      setConfig({
        region: regionName,
        carbonIntensity: preset.carbonIntensity,
        pue: preset.pue,
        wue: preset.wue,
      });
    } else {
      setConfig({ region: regionName });
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }} role="region" aria-labelledby="section-infra">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon color="action" />
          <Typography variant="h6" component="h2" fontWeight="bold" id="section-infra">
            {i18n.t('settings.sustainablePlatform.infrastructureConfig')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            size="small"
            label={`${config.provider} — ${config.region}`}
            sx={{ fontSize: '0.75rem' }}
          />
          <Chip
            size="small"
            label={`${config.carbonIntensity} gCO2/kWh`}
            color={config.carbonIntensity < 100 ? 'success' : config.carbonIntensity < 250 ? 'warning' : 'error'}
            sx={{ fontSize: '0.75rem' }}
          />
          <IconButton size="small" onClick={() => setExpanded(!expanded)} aria-label={expanded ? i18n.t('settings.sustainablePlatform.collapseConfig') : i18n.t('settings.sustainablePlatform.expandConfig')}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded} timeout="auto">
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            {/* Provider */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Cloud Provider</InputLabel>
                <Select
                  value={config.provider}
                  label="Cloud Provider"
                  onChange={(e) => handleProviderChange(e.target.value)}
                >
                  {providers.map((p) => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Region */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{i18n.t('settings.sustainablePlatform.region')}</InputLabel>
                <Select
                  value={config.region}
                  label={i18n.t('settings.sustainablePlatform.region')}
                  onChange={(e) => handleRegionChange(e.target.value)}
                >
                  {regions.map((r) => (
                    <MenuItem key={r.name} value={r.name}>
                      {r.name} ({r.carbonIntensity} gCO2/kWh)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Instance type */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label={i18n.t('settings.sustainablePlatform.instanceType')}
                value={config.instanceType}
                onChange={(e) => setConfig({ instanceType: e.target.value })}
              />
            </Grid>

            {/* Database */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Database"
                value={config.database}
                onChange={(e) => setConfig({ database: e.target.value })}
              />
            </Grid>

            {/* PUE */}
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                label="PUE"
                type="number"
                inputProps={{ step: 0.01, min: 1.0, max: 3.0 }}
                value={config.pue}
                onChange={(e) => setConfig({ pue: parseFloat(e.target.value) || DEFAULT_CONFIG.pue })}
              />
            </Grid>

            {/* Carbon Intensity */}
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                label="CI (gCO2/kWh)"
                type="number"
                inputProps={{ step: 1, min: 0 }}
                value={config.carbonIntensity}
                onChange={(e) => setConfig({ carbonIntensity: parseFloat(e.target.value) || DEFAULT_CONFIG.carbonIntensity })}
              />
            </Grid>

            {/* WUE */}
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                label="WUE (L/kWh)"
                type="number"
                inputProps={{ step: 0.1, min: 0 }}
                value={config.wue}
                onChange={(e) => setConfig({ wue: parseFloat(e.target.value) || DEFAULT_CONFIG.wue })}
              />
            </Grid>

            {/* Server lifetime */}
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                label={i18n.t('settings.sustainablePlatform.serverLifeYears')}
                type="number"
                inputProps={{ step: 1, min: 1, max: 10 }}
                value={config.serverLifetimeYears}
                onChange={(e) => setConfig({ serverLifetimeYears: parseInt(e.target.value) || DEFAULT_CONFIG.serverLifetimeYears })}
              />
            </Grid>

            {/* Total host vCPUs */}
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                label="vCPU host"
                type="number"
                inputProps={{ step: 1, min: 1 }}
                value={config.totalHostVCPUs}
                onChange={(e) => setConfig({ totalHostVCPUs: parseInt(e.target.value) || DEFAULT_CONFIG.totalHostVCPUs })}
              />
            </Grid>

            {/* Server embodied */}
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Embodied (kgCO2e)"
                type="number"
                inputProps={{ step: 100, min: 0 }}
                value={config.serverEmbodiedKgCO2}
                onChange={(e) => setConfig({ serverEmbodiedKgCO2: parseFloat(e.target.value) || DEFAULT_CONFIG.serverEmbodiedKgCO2 })}
              />
            </Grid>
          </Grid>

          {/* Reset button */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              startIcon={<ResetIcon />}
              onClick={resetToDefaults}
              color="inherit"
            >
              {i18n.t('settings.sustainablePlatform.resetDefaults')}
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
}

// ─── Section 7: Note Metodologiche ────────────────────────
function MethodologyNotesSection({ lang }: { lang: string }) {
  void lang;
  const { t } = useTranslation();
  const sections = [
    {
      title: t('settings.sustainablePlatformPage.methodologySiaTitle'),
      text: t('settings.sustainablePlatformPage.methodologySiaText'),
    },
    {
      title: t('settings.sustainablePlatformPage.methodologyGsfTitle'),
      text: t('settings.sustainablePlatformPage.methodologyGsfText'),
    },
    {
      title: t('settings.sustainablePlatformPage.methodologySciTitle'),
      text: t('settings.sustainablePlatformPage.methodologySciText'),
    },
    {
      title: t('settings.sustainablePlatformPage.methodologyCloudTitle'),
      text: t('settings.sustainablePlatformPage.methodologyCloudText'),
    },
    {
      title: t('settings.sustainablePlatformPage.methodologyBiodiversityTitle'),
      text: t('settings.sustainablePlatformPage.methodologyBiodiversityText'),
    },
    {
      title: t('settings.sustainablePlatformPage.methodologyLimitationsTitle'),
      text: t('settings.sustainablePlatformPage.methodologyLimitationsText'),
    },
  ];

  return (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }} role="region" aria-labelledby="section-methodology">
      <SectionTitle
        id="section-methodology"
        icon={<DescriptionIcon color="action" />}
        title={t('settings.sustainablePlatformPage.methodologyNotes')}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {sections.map((sec) => (
          <Box key={sec.title}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
              {sec.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {sec.text}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

// ─── Main Page ───────────────────────────────────────────
export function SustainablePlatformPage() {
  const { year } = useWorkingYear();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const handleDownloadPDF = async () => {
    setGeneratingPdf(true);
    try {
      const { pdf } = await import('@react-pdf/renderer');
      const { SustainabilityReportPDF } = await import('../reporting/components/pdf/SustainabilityReportPDF');
      const blob = await pdf(<SustainabilityReportPDF year={year} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Sustainability_Assessment_${year}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Sustainability PDF generation failed:', err);
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          {i18n.t('settings.sustainablePlatform.pageTitle')}
        </Typography>
        <Chip label={year} color="primary" />
        <Button
          variant="contained"
          color="success"
          startIcon={generatingPdf ? <CircularProgress size={18} color="inherit" /> : <PdfIcon />}
          onClick={handleDownloadPDF}
          disabled={generatingPdf}
          sx={{ ml: 'auto' }}
        >
          {generatingPdf
            ? i18n.t('settings.sustainablePlatform.generatingPdf')
            : i18n.t('settings.sustainablePlatform.downloadPdf')
          }
        </Button>
      </Box>

      {/* Infrastructure configuration */}
      <Box sx={{ mb: 3 }}>
        <InfrastructureConfigSection lang={lang} />
      </Box>

      {/* Overall verdict */}
      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600}>PASS</Typography>
        <Typography variant="body2">
          {t('settings.sustainablePlatformPage.overallVerdict')}
        </Typography>
      </Alert>

      {/* 7 Sections */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <SIASection lang={lang} />
        <GSFSection lang={lang} />
        <CarbonAwareSection lang={lang} />
        <SCISection lang={lang} />
        <CloudEmissionSection lang={lang} />
        <BiodiversitySection lang={lang} />
        <MethodologyNotesSection lang={lang} />
      </Box>
    </Box>
  );
}
